import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, IconButton, Tooltip } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import HistoryIcon from '@mui/icons-material/History';

import { translate as t } from 'hooks/useTranslations';
import { neutral } from '_lex/_foundations/phxColors';
import type { DTBPackage } from 'views/App/Portal/DTB/_types/dtb';
import {
  dtbPackageHasDraftDtbs,
  isDtbPackageEligibleForAssignmentOrDistribution,
} from 'views/App/Portal/DTB/utils/dtbPackageEligibility';

export type PackageAssignmentRowActionsProps = {
  row: DTBPackage;
};

const PackageAssignmentRowActions: React.FC<PackageAssignmentRowActionsProps> = ({ row }) => {
  const navigate = useNavigate();
  const isApproved = (row.status || '').toUpperCase() === 'APPROVED';
  const canAssign = isDtbPackageEligibleForAssignmentOrDistribution(row);

  const assignTooltipEnabled = t('dtbs.assignments.actions.assignTooltipEnabled');
  const assignTooltipDisabled = t('dtbs.assignments.actions.assignTooltipDisabled');
  const assignTooltipDraftDtbs = t('dtbs.assignments.actions.assignTooltipDraftDtbs');
  const historyTooltip = t('dtbs.assignments.actions.historyTooltip');

  const assignTooltip = !isApproved
    ? assignTooltipDisabled
    : dtbPackageHasDraftDtbs(row)
      ? assignTooltipDraftDtbs
      : assignTooltipEnabled;

  const handleAssignClick = () => {
    if (canAssign) {
      navigate(`/admin/dtbs/assignments/${row.id}/assign`);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
      <Tooltip title={assignTooltip}>
        <span>
          <Button
            variant='text'
            size='small'
            color='primary'
            disabled={!canAssign}
            endIcon={<ArrowForwardIcon />}
            onClick={handleAssignClick}
            sx={{
              '&.Mui-disabled': {
                color: neutral[400],
              },
            }}
          >
            {t('dtbs.assignments.actions.assignButton')}
          </Button>
        </span>
      </Tooltip>
      <Tooltip title={historyTooltip}>
        <IconButton
          size='small'
          onClick={() => navigate(`/admin/dtbs/assignments/${row.id}/history`)}
          aria-label={historyTooltip}
          sx={{
            borderRadius: 1,
          }}
        >
          <HistoryIcon fontSize='small' />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default PackageAssignmentRowActions;
